import { Box, Flex, Group, SimpleGrid, Text, UnstyledButton } from "@mantine/core";
import {
    IconChevronRight, IconWorld, IconPalette, IconCar, IconSparkles,
    IconBook, IconBriefcase, IconMoodSmile, IconCut, IconSchool,
    IconCalendarEvent, IconHanger, IconCoin, IconActivity, IconChefHat,
    IconDeviceGamepad, IconBuildingBank, IconHome, IconCoffee, IconPlant,
    IconMusic, IconUsers, IconPaw, IconCamera, IconFlask, IconHeart,
    IconBallFootball, IconLeaf, IconDeviceLaptop, IconPlane, IconMovie,
    IconRobot, IconFolder
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useState } from "react";
import { CATEGORIES } from "../../../constants/options";
import { primaryColor } from "../../../constants/theme";

const getCategoryIcon = (categoryName: string, color: string) => {
    const props = { size: 20, color, stroke: 1.5 };
    switch (categoryName.toLowerCase()) {
        case 'activism & social causes': return <IconWorld {...props} />;
        case 'art & design': return <IconPalette {...props} />;
        case 'automotive': return <IconCar {...props} />;
        case 'beauty & makeup': return <IconSparkles {...props} />;
        case 'books & literature': return <IconBook {...props} />;
        case 'business & entrepreneurship': return <IconBriefcase {...props} />;
        case 'comedy & entertainment': return <IconMoodSmile {...props} />;
        case 'diy & crafts': return <IconCut {...props} />;
        case 'education & learning': return <IconSchool {...props} />;
        case 'events & social gatherings': return <IconCalendarEvent {...props} />;
        case 'fashion & style': return <IconHanger {...props} />;
        case 'finance & investing': return <IconCoin {...props} />;
        case 'fitness & health': return <IconActivity {...props} />;
        case 'food & cooking': return <IconChefHat {...props} />;
        case 'gaming': return <IconDeviceGamepad {...props} />;
        case 'history & culture': return <IconBuildingBank {...props} />;
        case 'home decor & interior design': return <IconHome {...props} />;
        case 'lifestyle': return <IconCoffee {...props} />;
        case 'motivational & self-improvement': return <IconPlant {...props} />;
        case 'music': return <IconMusic {...props} />;
        case 'parenting & family': return <IconUsers {...props} />;
        case 'pets & animals': return <IconPaw {...props} />;
        case 'photography & videography': return <IconCamera {...props} />;
        case 'science & technology': return <IconFlask {...props} />;
        case 'spirituality & mindfulness': return <IconHeart {...props} />;
        case 'sports & outdoor activities': return <IconBallFootball {...props} />;
        case 'sustainable living & eco-friendly practices': return <IconLeaf {...props} />;
        case 'technology & gadgets': return <IconDeviceLaptop {...props} />;
        case 'travel & adventure': return <IconPlane {...props} />;
        case 'tv & film': return <IconMovie {...props} />;
        case 'ai': return <IconRobot {...props} />;
        default: return <IconFolder {...props} />;
    }
};

const CategoryItem = ({ cat, onClickOption }: { cat: any; onClickOption: (category: string) => void }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <UnstyledButton
            onClick={() => onClickOption(cat.name)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                width: '100%',
                border: `1px solid ${hovered ? primaryColor : 'var(--mantine-color-gray-3)'}`,
                borderRadius: 'var(--mantine-radius-md)',
                padding: '12px 16px',
                backgroundColor: hovered ? `${primaryColor}1A` : 'transparent',
                transition: 'all 0.2s ease',
            }}
        >
            <Flex justify="space-between" align="center">
                <Group gap="md">
                    <Box
                        bg={hovered ? "white" : "gray.1"}
                        style={{
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 36,
                            height: 28,
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {getCategoryIcon(cat.name, primaryColor)}
                    </Box>
                    <Text fw={500} size="md" c={hovered ? primaryColor : "dark.7"} style={{ transition: 'all 0.2s ease' }}>
                        {cat.name}
                    </Text>
                </Group>
                <IconChevronRight size={20} color={hovered ? primaryColor : "var(--mantine-color-gray-4)"} style={{ transition: 'all 0.2s ease' }} />
            </Flex>
        </UnstyledButton>
    );
};

export const CategorySelector = ({ onClickOption }: { onClickOption: (category: string) => void }) => {
    return (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {CATEGORIES.map((cat, index) => (
                <motion.div
                    key={cat.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                    <CategoryItem cat={cat} onClickOption={onClickOption} />
                </motion.div>
            ))}
        </SimpleGrid>
    );
};